import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Default Props & PropTypes
//  Chapter 2 · Components
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_0 = `// Method 1 — Destructuring defaults (RECOMMENDED for functional components)
function Button({ label = 'Click Me', variant = 'primary', size = 'md', disabled = false }) {
  return (
    <button
      className={\`btn btn-\${variant} btn-\${size}\`}
      disabled={disabled}
    >
      {label}
    </button>
  );
}

// Method 2 — defaultProps static property (older style, still works)
Button.defaultProps = {
  label:    'Click Me',
  variant:  'primary',
  size:     'md',
  disabled: false,
};`;

const CODE_1 = `npm install prop-types`;

const CODE_2 = `import PropTypes from 'prop-types';

function UserProfile({ name, age, email, role, scores, address, onEdit }) {
  return ( /* ... JSX ... */ );
}

UserProfile.propTypes = {
  name:    PropTypes.string.isRequired,       // required string
  age:     PropTypes.number,                  // optional number
  email:   PropTypes.string.isRequired,
  role:    PropTypes.oneOf(['admin', 'user', 'guest']),  // specific values
  scores:  PropTypes.arrayOf(PropTypes.number),           // array of numbers
  address: PropTypes.shape({                              // object shape
    city:    PropTypes.string,
    country: PropTypes.string,
    zip:     PropTypes.string,
  }),
  onEdit:  PropTypes.func,                    // function
  children: PropTypes.node,                   // anything renderable
};

UserProfile.defaultProps = {
  role:   'user',
  scores: [],
  onEdit: () => {},
};

export default UserProfile;`;

const CODE_3 = `// With TypeScript — no PropTypes library needed
interface UserProfileProps {
  name:     string;
  age?:     number;              // ? = optional
  email:    string;
  role?:    'admin' | 'user' | 'guest';
  scores?:  number[];
  onEdit?:  () => void;
}

function UserProfile({ name, age, email, role = 'user', scores = [], onEdit }: UserProfileProps) {
  return ( /* JSX */ );
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function DefaultPropsAndPropTypes() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 2 · Components</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Default Props & PropTypes</h1>
        <p style={styles.desc}>Make your components safer and more predictable with default values and runtime type checking.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── Why Default Props? ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Why Default Props?</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Default props ensure a component still works correctly when certain props aren&#39;t provided by the parent. They&#39;re your safety net — defining the minimum viable state of a component." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_0, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_0)}</pre>
        </div>
      </div>

      /* ── Installing and Using PropTypes ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Installing and Using PropTypes</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "PropTypes provide runtime type checking in development. When a wrong type is passed, React logs a clear warning in the browser console — invaluable for catching bugs early." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── PropTypes in Practice ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>PropTypes in Practice</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Define the shape of your props with PropTypes. This also serves as built-in documentation — any developer can look at propTypes to understand what a component expects." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 2)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 2 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── TypeScript Alternative ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>TypeScript Alternative</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "In TypeScript projects, you define prop types with an interface or type — providing compile-time checking instead of runtime. This is now the industry preference." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_3, 3)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 3 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_3)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. What happens if a required PropType is not provided?</div>
          <div style={styles.qItem}>✦ 2. Are PropTypes checked in production builds?</div>
          <div style={styles.qItem}>✦ 3. What\'s the difference between PropTypes.node and PropTypes.element?</div>
          <div style={styles.qItem}>✦ 4. How do you type-check an array of objects with PropTypes?</div>
          <div style={styles.qItem}>✦ 5. Why might you choose TypeScript interfaces over PropTypes?</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
